import * as Sentry from '@sentry/react';
import {
  AppStore,
  CurrentUserStore,
  PitstopCookie,
  FeatureFlagStore,
} from 'stores';
import { notification } from 'antd';
import { singletonCacheAPIStore } from 'stores/CacheAPIStore';
import FetchCommonErrorsEnum from './enums/FetchCommonErrorsEnum';
import { isValidDataForCaching } from 'helpers/cacheValidation';
import authTokenManager from '../services/AuthTokenManager';

export const webServiceProvider = {
  async delete(endpoint, signal) {
    return _delete(endpoint, signal);
  },
  async get(url, signal) {
    return _getUsingUrlOnly(url, signal);
  },
  async getById(endpoint, id) {
    return _get(`${endpoint}/${id}`);
  },
  async getMany(endpoint, params = {}, signal) {
    return _get(endpoint, params, signal);
  },

  clearCache() {
    return singletonCacheAPIStore.reset();
  },

  getCacheStats() {
    return singletonCacheAPIStore.getCacheStats();
  },

  invalidateCachePattern(pattern) {
    return singletonCacheAPIStore.invalidatePattern(pattern);
  },

  invalidateCacheEndpoint(endpoint) {
    return singletonCacheAPIStore.invalidateEndpoint(endpoint);
  },

  invalidateCacheResource(resourceType, resourceId) {
    const pattern = `/${resourceType}/${resourceId}`;
    return singletonCacheAPIStore.invalidatePattern(pattern);
  },
  async patch(endpoint, body) {
    return _patch(endpoint, body);
  },
  async post(endpoint, body, signal) {
    return _post(endpoint, body, signal);
  },
  async postFormData(endpoint, formData) {
    return _postFormData(endpoint, formData);
  },
  async put(endpoint, body) {
    return _put(endpoint, body);
  },
  postCustomEndpoint(endpoint, body, signal) {
    return _directFetchWithMethod(endpoint, 'POST', body, signal);
  },
  directFetchWithMethod(endpoint, method, body = {}, signal) {
    return _directFetchWithMethod(endpoint, method, body, signal);
  },
  fetchCSV: _fetchCSV,
  getPDF,
};

const _apiBaseUrl = `${import.meta.env.VITE_API_URL}`;
const _prefix = '';

/**
 * Capture API errors and send them to Sentry with context
 * @param {Error} error - The error object
 * @param {string} endpoint - The API endpoint that failed
 * @param {string} method - The HTTP method used
 * @param {Object} additionalContext - Additional context to include
 */
function captureApiError(error, endpoint, method, additionalContext = {}) {
  // Only skip truly non-errors like aborted requests
  if (error?.message === FetchCommonErrorsEnum.ABORTED_REQUEST) return;

  // Capture the error with rich context
  Sentry.captureException(error, {
    tags: {
      component: 'webServiceProvider',
      api_method: method,
      status_code: error?.statusCode || error?.code || 'unknown',
      endpoint: endpoint,
    },
    extra: {
      endpoint,
      method,
      error_details: error?.details,
      error_message: error?.message,
      api_base_url: _apiBaseUrl,
      ...additionalContext,
    },
    fingerprint: ['api-error', method, String(error?.code || 'unknown')],
  });
}

/**
 * Get cache invalidation patterns based on the modified endpoint
 * This ensures that when a resource is modified, all related cached data is invalidated
 */
function getInvalidationPatterns(endpointPath) {
  const patterns = [endpointPath]; // Always invalidate the exact endpoint

  // Work Order related invalidations
  if (endpointPath.includes('/work-order')) {
    // Extract shop ID if present in the URL
    const shopIdMatch =
      endpointPath.match(/shopId=(\d+)/) ||
      endpointPath.match(/id_shop=(\d+)/) ||
      endpointPath.match(/\/shops?\/?(\d+)/) ||
      endpointPath.match(/fleet\/(\d+)/);

    if (shopIdMatch) {
      const shopId = shopIdMatch[1];
      patterns.push(
        '/v1/work-order/statuses-by-shop-id', // Status counts (all shops)
        `/v1/work-order/fleet/${shopId}`, // Fleet data for specific shop
        `/v1/work-order?id_shop=${shopId}`, // Work order lists for specific shop
        `v1/work-order/fleet/${shopId}`, // Fleet data (relative URL)
        `v1/work-order?id_shop=${shopId}`, // Work order lists (relative URL)
        'work-order' // General work order pattern
      );
    } else {
      // If no shop ID found, invalidate common work order patterns
      patterns.push(
        '/v1/work-order',
        'v1/work-order',
        'work-order',
        '/v1/work-order/search',
        'v1/work-order/search'
      );
    }
  }

  // Car related invalidations
  if (endpointPath.includes('/car')) {
    patterns.push('/v1/car', 'v1/car', 'car');
  }

  // Shop related invalidations
  if (endpointPath.includes('/shop')) {
    patterns.push('/v1/shop', 'v1/shop', 'shop');
  }

  // Appointment related invalidations
  if (endpointPath.includes('/appointment')) {
    patterns.push('/v1/appointment', 'v1/appointment', 'appointment');
  }

  // Issue related invalidations
  if (endpointPath.includes('/issue')) {
    patterns.push('/v1/issue', 'v1/issue', 'issue');
  }

  // DVIR related invalidations
  if (
    endpointPath.includes('/dvir') ||
    endpointPath.includes('work-order-dvir')
  ) {
    patterns.push(
      '/v1/dvirs',
      'v1/dvirs',
      'dvir',
      '/work-order-dvir', // DVIR work order searches
      'work-order-dvir'
    );

    // Extract shop ID for shop-specific DVIR invalidation
    const shopIdMatch =
      endpointPath.match(/shopId=(\d+)/) ||
      endpointPath.match(/shops?\/?(\d+)/);

    if (shopIdMatch) {
      const shopId = shopIdMatch[1];
      patterns.push(
        `/v1/dvirs/shop?shopId=${shopId}`,
        `/v1/dvirs/stats?shopId=${shopId}`,
        `/v1/dvirs/details?shopId=${shopId}`,
        `/v1/dvirs/availability-mapping?shopId=${shopId}`,
        `/v1/dvirs/latest?shopId=${shopId}`,
        `v1/dvirs/shop?shopId=${shopId}`, // Relative URL version
        `v1/dvirs/stats?shopId=${shopId}`, // Relative URL version
        `v1/dvirs/details?shopId=${shopId}`, // Relative URL version
        `v1/dvirs/availability-mapping?shopId=${shopId}`, // Relative URL version
        `v1/dvirs/latest?shopId=${shopId}` // Relative URL version
      );

      // Work order related invalidations
      patterns.push(
        '/v1/work-order/statuses-by-shop-id', // Status counts (all shops)
        `/v1/work-order/fleet/${shopId}`, // Fleet data for specific shop
        `/v1/work-order?id_shop=${shopId}`, // Work order lists for specific shop
        `v1/work-order/fleet/${shopId}`, // Fleet data (relative URL)
        `v1/work-order?id_shop=${shopId}`, // Work order lists (relative URL)
        'work-order' // General work order pattern
      );
    }

    // Also invalidate by car ID if present
    const carIdMatch = endpointPath.match(/carIds?=(\d+)/);
    if (carIdMatch) {
      const carId = carIdMatch[1];
      patterns.push(
        `/v1/dvirs/latest?carIds=${carId}`,
        `v1/dvirs/latest?carIds=${carId}`,
        `/v1/dvirs/latest?carIds=${carId}&trailers=true`,
        `v1/dvirs/latest?carIds=${carId}&trailers=true`
      );
    }
    // If no shop ID found, invalidate common work order patterns
    patterns.push(
      '/v1/work-order',
      'v1/work-order',
      'work-order',
      '/v1/work-order/search',
      'v1/work-order/search'
    );
  }

  // Contact related invalidations
  if (endpointPath.includes('/contact')) {
    patterns.push('/v1/contact', 'v1/contact', 'contact');
  }

  // Integration related invalidations
  if (endpointPath.includes('/integration')) {
    patterns.push('/v1/integration', 'v1/integration', 'integration');
  }

  // Purchase order related invalidations
  if (endpointPath.includes('/purchase-order')) {
    patterns.push('/v1/purchase-order', 'v1/purchase-order', 'purchase-order');
  }

  // Vehicle part related invalidations
  if (endpointPath.includes('/vehicle-part')) {
    // Extract shop ID if present in the URL
    const shopIdMatch =
      endpointPath.match(/shopIds?=([\d,]+)/) ||
      endpointPath.match(/shops?\/?(\d+)/);

    if (shopIdMatch) {
      const shopIds = shopIdMatch[1].split(',');
      for (const shopId of shopIds) {
        patterns.push(
          `/v1/shops/${shopId}/subfleetEcosystem`, // Subfleet ecosystem data
          `v1/shops/${shopId}/subfleetEcosystem`, // Relative URL
          '/v1/shops/shop-basic-data', // Shop basic data (often includes all shops)
          'v1/shops/shop-basic-data' // Relative URL
        );
      }
    }

    // General vehicle part patterns
    patterns.push(
      '/v1/vehicle-part',
      'v1/vehicle-part',
      'vehicle-part',
      '/v1/shops', // Shop-related data that might depend on vehicle parts
      'v1/shops',
      'subfleetEcosystem' // Any subfleet ecosystem data
    );
  }

  // Service schedule related invalidations
  if (endpointPath.includes('/service-schedule')) {
    patterns.push(
      '/v1/service-schedule',
      'v1/service-schedule',
      'service-schedule'
    );
  }

  // Inspection related invalidations
  if (endpointPath.includes('/inspection')) {
    patterns.push('/v1/inspection', 'v1/inspection', 'inspection');
  }

  // Report related invalidations
  if (endpointPath.includes('/report')) {
    patterns.push('/v1/report', 'v1/report', 'report');
  }

  // Warranty related invalidations
  if (endpointPath.includes('/warrant')) {
    // Extract shop ID if present in the URL
    const shopIdMatch =
      endpointPath.match(/shopId=(\d+)/) ||
      endpointPath.match(/shops?\/?(\d+)/);

    if (shopIdMatch) {
      const shopId = shopIdMatch[1];
      patterns.push(
        `/v1/warranties?shopId=${shopId}`, // Warranties for specific shop
        `v1/warranties?shopId=${shopId}`, // Relative URL
        '/v1/warranties', // General warranties endpoint
        'v1/warranties' // Relative URL
      );
    } else {
      // If no shop ID found, invalidate general warranty patterns
      patterns.push(
        '/v1/warranties',
        'v1/warranties',
        'warrant' // General warranty pattern (covers warranties, warranty, etc.)
      );
    }
  }

  // User related invalidations
  if (endpointPath.includes('/user') || endpointPath.includes('user')) {
    // Extract shop ID if present in the URL or endpoint
    const shopIdMatch =
      endpointPath.match(/shopId=(\d+)/) ||
      endpointPath.match(/shops?\/?(\d+)/) ||
      endpointPath.match(/add-user-to-shop/) || // Special case for add-user-to-shop
      endpointPath.match(/remove-user-from-shop/);

    if (shopIdMatch) {
      // For user-to-shop operations, we need to invalidate user lists for all related shops
      // since we don't always know the specific shop ID from the endpoint
      patterns.push(
        '/v1/user', // General user endpoint (all variations)
        'v1/user', // Relative URL
        'user', // General user pattern
        '/user', // Alternative user endpoint format
        '/v1/users', // Plural version
        'v1/users', // Relative URL
        'users' // General users pattern
      );

      // If we can extract a specific shop ID, add shop-specific patterns
      if (shopIdMatch[1] && !isNaN(shopIdMatch[1])) {
        const shopId = shopIdMatch[1];
        patterns.push(
          `/v1/user?shopId=${shopId}`, // Users for specific shop
          `v1/user?shopId=${shopId}`, // Relative URL
          `/user?shopId=${shopId}`, // Alternative format
          `user?shopId=${shopId}` // Relative alternative
        );
      }
    } else {
      // General user invalidations
      patterns.push(
        '/v1/user',
        'v1/user',
        'user',
        '/user',
        '/v1/users',
        'v1/users',
        'users'
      );
    }
  }

  // Notification related invalidations
  if (endpointPath.includes('/notification')) {
    // Extract shop ID from various patterns
    const shopIdMatch =
      endpointPath.match(/notification\/(\d+)/) ||
      endpointPath.match(/shopId=(\d+)/) ||
      endpointPath.match(/id_shop=(\d+)/);

    if (shopIdMatch) {
      const shopId = shopIdMatch[1];
      patterns.push(
        `/v1/notification/report/shop?shopId=${shopId}`,
        `v1/notification/report/shop?shopId=${shopId}`,
        `/v1/notification/work-order/shop?shopId=${shopId}`,
        `v1/notification/work-order/shop?shopId=${shopId}`,
        '/v1/notification',
        'v1/notification',
        'notification'
      );
    } else {
      // If no shop ID found, invalidate general notification patterns
      patterns.push(
        '/v1/notification',
        'v1/notification',
        'notification',
        '/v1/notification/report',
        'v1/notification/report',
        '/v1/notification/work-order',
        'v1/notification/work-order'
      );
    }
  }

  return [...new Set(patterns)]; // Remove duplicates
}

/**
 * Shared helper for cache invalidation after mutations
 * @param {string} method - HTTP method
 * @param {string} endpoint - Full URL or endpoint path
 * @returns {Promise<void>}
 */
async function invalidateCacheAfterMutation(method, endpoint) {
  if (!FeatureFlagStore.isApiCachingEnabled) {
    return;
  }

  try {
    // Extract endpoint path from full URL for invalidation
    const endpointPath = endpoint.startsWith('http')
      ? new URL(endpoint).pathname
      : endpoint;

    const invalidationPatterns = getInvalidationPatterns(endpointPath);
    let totalInvalidated = 0;

    for (const pattern of invalidationPatterns) {
      const invalidatedCount =
        await singletonCacheAPIStore.invalidatePattern(pattern);
      totalInvalidated += invalidatedCount;
    }

    console.log(
      `🗑️ Cache invalidated after ${method} to ${endpointPath} (${totalInvalidated} entries, patterns: ${invalidationPatterns.join(', ')})`
    );
  } catch (error) {
    console.warn('Cache invalidation error:', error);
    try {
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('component', 'webServiceProvider');
        scope.setExtra('method', method);
        scope.setExtra('endpoint', endpoint);
        scope.setExtra('errorMessage', error?.message);
        Sentry.captureMessage('Cache invalidation error');
      });
    } catch (_) {
      // Intentionally ignore Sentry failures to avoid impacting flow
    }
  }
}

let networkErrorToastShown = false;
function showNetworkError() {
  if (networkErrorToastShown) return;
  networkErrorToastShown = true;
  notification.error({
    message: 'Network error',
    description: 'Please check your internet connection and try again.',
    placement: 'bottomRight',
    duration: 20,
    onClose: () => {
      networkErrorToastShown = false;
    },
  });
}

async function _delete(endpoint, signal) {
  const result = await _fetchWithMethod(endpoint, {}, 'DELETE', signal);
  await invalidateCacheAfterMutation('DELETE', endpoint);
  return result;
}

async function _directFetchWithMethod(
  endpoint,
  method,
  body,
  signal,
  maxRetries = 5,
  isRetryAfterAuth = false
) {
  if (!(await authTokenManager.validateAuthState())) {
    return;
  }

  // For GET requests, check cache first (if feature flag is enabled)
  if (method === 'GET' && FeatureFlagStore.isApiCachingEnabled) {
    const cacheKey = singletonCacheAPIStore.generateKey(endpoint, method, body);
    const cachedData = await singletonCacheAPIStore.getData(cacheKey);
    if (cachedData) {
      return cachedData;
    }
  }

  let accessToken;

  if (AppStore.isOnGeotabDashboard()) {
    accessToken = await PitstopCookie.get('accessToken');
  } else {
    accessToken = PitstopCookie.get('accessToken');
  }

  if (
    ![
      '/',
      '/login',
      '/login/geotab',
      '/register',
      '/reset-password',
      '/callback',
      '/callbackEntra',
    ].includes(window.location.pathname) &&
    !accessToken
  ) {
    authTokenManager.forceLogout(
      'Your token has expired. Please log in again!'
    );
    return;
  }

  const bodyData = {};

  if (method && !['GET'].includes(method)) {
    bodyData.body = JSON.stringify(body);
  }

  try {
    const start = performance.now();
    const response = await fetch(endpoint, {
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'client-id': import.meta.env.VITE_CLIENT_ID,
      },
      method,
      ...bodyData,
      ...(signal && { signal }),
    });
    const duration = performance.now() - start;

    if (response.status === 401 && !isRetryAfterAuth) {
      console.log('Received 401 in directFetch, attempting token refresh...');

      try {
        await authTokenManager.handleTokenRefresh();

        console.log('Token refreshed successfully, retrying direct fetch...');
        return _directFetchWithMethod(
          endpoint,
          method,
          body,
          signal,
          maxRetries,
          true
        );
      } catch (authError) {
        console.error('Token refresh failed in directFetch:', authError);
        // Report auth failures to Sentry as these are critical
        Sentry.captureException(authError, {
          tags: {
            component: 'webServiceProvider',
            error_type: 'token_refresh_failed',
            endpoint: endpoint,
          },
          level: 'error',
        });
        throw authError;
      }
    }

    if (response.status !== 200 && response.status !== 201) {
      const contentType = response.headers.get('Content-Type');
      const error = new Error();
      error.statusCode = response.status;
      error.code = response.status;

      if (contentType && contentType.includes('application/json')) {
        try {
          const jsonError = await response.json();
          error.message =
            jsonError.message || jsonError.error || JSON.stringify(jsonError);
          error.details = jsonError;
        } catch (e) {
          // If JSON parsing fails, fall back to text
          const text = await response.text();
          error.message = text;
        }
      } else {
        const text = await response.text();
        error.message = text;
      }
      throw error;
    }

    const result = await response
      .json()
      .then((json) => json)
      .catch(() => null);

    // Cache successful GET responses (if feature flag is enabled)
    if (
      method === 'GET' &&
      isValidDataForCaching(result) &&
      FeatureFlagStore.isApiCachingEnabled
    ) {
      const cacheKey = singletonCacheAPIStore.generateKey(
        endpoint,
        method,
        body
      );
      try {
        await singletonCacheAPIStore.setData(cacheKey, result);
      } catch (error) {
        console.warn('Cache API write error:', error);
        try {
          Sentry.withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag('component', 'webServiceProvider');
            scope.setExtra('cacheKey', cacheKey);
            scope.setExtra('errorMessage', error?.message);
            Sentry.captureMessage('Cache API write error');
          });
        } catch (_) {
          // Intentionally ignore Sentry failures to avoid impacting flow
        }
      }
    }

    // Invalidate cache for mutating operations (if feature flag is enabled)
    if (
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) &&
      FeatureFlagStore.isApiCachingEnabled
    ) {
      // Extract endpoint path from full URL for invalidation
      const urlObj = new URL(endpoint);
      const endpointPath = urlObj.pathname;

      // Smart cache invalidation: invalidate related endpoints based on the modified resource
      const invalidationPatterns = getInvalidationPatterns(endpointPath);

      let totalInvalidated = 0;
      for (const pattern of invalidationPatterns) {
        const invalidatedCount =
          await singletonCacheAPIStore.invalidatePattern(pattern);
        totalInvalidated += invalidatedCount;
      }

      console.log(
        `🗑️ Cache invalidated after ${method} to ${endpointPath} (${totalInvalidated} entries, patterns: ${invalidationPatterns.join(', ')})`
      );
    }

    return result;
  } catch (error) {
    if (!window.navigator.onLine || error.message === 'Failed to fetch') {
      console.error(error);
      showNetworkError();
    }
    if (error?.statusCode === 401 || error?.code === 401) {
      // Report 401 errors immediately (no retries for auth failures)
      captureApiError(error, endpoint, method, {
        hasBody: !!body,
        isRetryAfterAuth,
        errorType: 'auth_failure',
      });
      throw error;
    }

    if (error?.code && parseInt(error?.code) < 500) {
      // Report 4xx client errors immediately (no retries for client errors)
      captureApiError(error, endpoint, method, {
        hasBody: !!body,
        isRetryAfterAuth,
        errorType: 'client_error',
      });
      throw error;
    }

    const shouldBypassRetries = [
      FetchCommonErrorsEnum.ABORTED_REQUEST,
      FetchCommonErrorsEnum.INVALID_INPUT_ERROR,
    ];
    if (!shouldBypassRetries.includes(error?.message) && maxRetries > 0) {
      console.info('retrying request...' + maxRetries);
      // sleep for 2 seconds
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return _directFetchWithMethod(
        endpoint,
        method,
        body,
        signal,
        --maxRetries,
        isRetryAfterAuth
      );
    }

    // Report server/network errors after all retries are exhausted
    captureApiError(error, endpoint, method, {
      hasBody: !!body,
      isRetryAfterAuth,
      errorType: 'server_error',
      retriesExhausted: true,
    });

    throw error;
  }
}

async function _fetch(
  endpoint,
  getFetchResponse,
  maxRetries = 5,
  isRetryAfterAuth = false
) {
  if (!(await authTokenManager.validateAuthState())) {
    return;
  }

  let accessToken;

  if (AppStore.isOnGeotabDashboard()) {
    accessToken = await PitstopCookie.get('accessToken');
  } else {
    accessToken = PitstopCookie.get('accessToken');
  }

  if (
    ![
      '/',
      '/login',
      '/login/geotab',
      '/register',
      '/reset-password',
      '/callback',
      '/callbackEntra',
    ].includes(window.location.pathname) &&
    !accessToken &&
    !window.location.search.includes('code') // This is for the oauth flow
  ) {
    authTokenManager.forceLogout(
      'Your token has expired. Please log in again!'
    );
    return;
  }

  const transformed = _transformUrl(endpoint);
  try {
    const response = await getFetchResponse(transformed);
    // If fetch returned no response (e.g., missing mock), treat as aborted to avoid long retries
    if (!response) {
      const err = new Error(FetchCommonErrorsEnum.ABORTED_REQUEST);
      err.code = 'NO_RESPONSE';
      throw err;
    }

    // Handle 401 Unauthorized responses
    if (response.status === 401 && !isRetryAfterAuth) {
      console.log('Received 401, attempting token refresh...');

      try {
        await authTokenManager.handleTokenRefresh();

        console.log(
          'Token refreshed successfully, retrying original request...'
        );
        return _fetch(endpoint, getFetchResponse, maxRetries, true);
      } catch (authError) {
        console.error('Token refresh failed:', authError);
        throw authError;
      }
    }

    // Safely parse body (allowing 204 without a body)
    let parsedBody = null;
    let rawText = '';
    const contentType = response.headers.get('Content-Type') || '';
    if (response.status !== 204) {
      try {
        if (contentType.includes('application/json')) {
          parsedBody = await response.json();
        } else {
          rawText = await response.text();
        }
      } catch (e) {
        parsedBody = null;
      }
    }

    // For scan/pids only, treat 204/404/"Empty result" as no-data (not an error)
    const isScanPids =
      String(endpoint).includes('/scan/pids') ||
      String(endpoint).includes('scan/pids');
    const messageStr =
      (parsedBody && (parsedBody.message || parsedBody.error)) || rawText || '';
    const looksLikeNoData =
      response.status === 204 ||
      response.status === 404 ||
      /empty result/i.test(String(messageStr));

    if (!response.ok) {
      if (isScanPids && looksLikeNoData) {
        // Return a standardized empty payload so callers can display the notification
        return { data: {}, meta: {}, __empty: true };
      }

      const error = new Error(messageStr || 'Request failed');
      error.statusCode = response.status;
      error.code = response.status;
      error.details = parsedBody || rawText;
      throw error;
    }

    // OK branch: return parsed JSON (or raw text if not JSON)
    return parsedBody !== null ? parsedBody : rawText;
  } catch (error) {
    if (error?.statusCode === 401 || error?.code === 401) {
      // Report 401 errors immediately (no retries for auth failures)
      captureApiError(error, endpoint, 'FETCH', {
        isRetryAfterAuth,
        errorType: 'auth_failure',
      });
      throw error;
    }

    if (error?.code && parseInt(error?.code) < 500) {
      // Report 4xx client errors immediately (no retries for client errors)
      captureApiError(error, endpoint, 'FETCH', {
        isRetryAfterAuth,
        errorType: 'client_error',
      });
      throw error;
    }

    const shouldBypassRetries = [
      FetchCommonErrorsEnum.ABORTED_REQUEST,
      'Token refresh failed',
    ];
    if (!shouldBypassRetries.includes(error?.message) && maxRetries > 0) {
      console.info('retrying request...' + maxRetries);
      // sleep for 2 seconds
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return _fetch(endpoint, getFetchResponse, --maxRetries, isRetryAfterAuth);
    }

    // Report server/network errors after all retries are exhausted
    captureApiError(error, endpoint, 'FETCH', {
      isRetryAfterAuth,
      errorType: 'server_error',
      retriesExhausted: true,
    });

    throw error;
  }
}

async function _fetchWithMethod(endpoint, body, method, signal) {
  const data = await _fetch(endpoint, async (transformedUrl) => {
    let accessToken;

    if (AppStore.isOnGeotabDashboard()) {
      accessToken = await PitstopCookie.get('accessToken');
    } else {
      accessToken = PitstopCookie.get('accessToken');
    }

    try {
      const start = performance.now();
      const response = await fetch(transformedUrl, {
        body: JSON.stringify(body),
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'client-id': import.meta.env.VITE_CLIENT_ID,
        },
        method,
        ...(signal && { signal }),
      });
      const duration = performance.now() - start;
      return response;
    } catch (error) {
      console.error(error);
      if (!window.navigator.onLine || error.message === 'Failed to fetch') {
        showNetworkError();
      }
    }
  });

  return data;
}

async function _get(endpoint, params = {}, signal) {
  // Generate cache key based on endpoint and params
  const transformedUrl = _transformUrl(endpoint);
  const url = new URL(transformedUrl);
  url.search = new URLSearchParams(params);
  const cacheKey = singletonCacheAPIStore.generateKey(url.toString(), 'GET');

  // Check cache first (if feature flag is enabled)
  if (FeatureFlagStore.isApiCachingEnabled) {
    try {
      const cachedData = await singletonCacheAPIStore.getData(cacheKey);
      if (cachedData !== null && cachedData !== undefined) {
        return cachedData;
      }
    } catch (error) {
      console.warn('Cache API read error:', error);
      try {
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('component', 'webServiceProvider');
          scope.setExtra('cacheKey', cacheKey);
          scope.setExtra('errorMessage', error?.message);
          Sentry.captureMessage('Cache API read error');
        });
      } catch (_) {
        // Intentionally ignore Sentry failures to avoid impacting flow
      }
    }
  }

  const data = await _fetch(endpoint, async (transformedUrl) => {
    const url = new URL(transformedUrl);
    url.search = new URLSearchParams(params);
    let accessToken;

    if (AppStore.isOnGeotabDashboard()) {
      accessToken = await PitstopCookie.get('accessToken');
    } else {
      accessToken = PitstopCookie.get('accessToken');
    }

    try {
      const start = performance.now();
      const response = await fetch(url, {
        mode: 'cors',
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'client-id': import.meta.env.VITE_CLIENT_ID,
        }),
        ...(signal && { signal }),
      });
      const duration = performance.now() - start;
      return response;
    } catch (error) {
      console.error(error);
      if (!window.navigator.onLine || error.message === 'Failed to fetch') {
        showNetworkError();
      }
    }
  });

  // Cache the successful response (if feature flag is enabled)

  if (
    (data && !data.__empty) ||
    (isValidDataForCaching(data) && FeatureFlagStore.isApiCachingEnabled)
  ) {
    try {
      await singletonCacheAPIStore.setData(cacheKey, data);
    } catch (error) {
      console.warn('Cache API write error:', error);
      try {
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('component', 'webServiceProvider');
          scope.setExtra('cacheKey', cacheKey);
          scope.setExtra('errorMessage', error?.message);
          Sentry.captureMessage('Cache API write error');
        });
      } catch (_) {
        // Intentionally ignore Sentry failures to avoid impacting flow
      }
    }
  }

  return data;
}

async function _getUsingUrlOnly(url, signal) {
  // Generate cache key for the full URL
  const cacheKey = singletonCacheAPIStore.generateKey(url, 'GET');

  // Check cache first (if feature flag is enabled)
  if (FeatureFlagStore.isApiCachingEnabled) {
    try {
      const cachedData = await singletonCacheAPIStore.getData(cacheKey);
      if (cachedData !== null && cachedData !== undefined) {
        return cachedData;
      }
    } catch (error) {
      console.warn('Cache API read error:', error);
      try {
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('component', 'webServiceProvider');
          scope.setExtra('cacheKey', cacheKey);
          scope.setExtra('errorMessage', error?.message);
          Sentry.captureMessage('Cache API read error');
        });
      } catch (_) {
        // Intentionally ignore Sentry failures to avoid impacting flow
      }
    }
  }

  const data = await _fetch(url, async (transformedUrl) => {
    let accessToken;

    if (AppStore.isOnGeotabDashboard()) {
      accessToken = await PitstopCookie.get('accessToken');
    } else {
      accessToken = PitstopCookie.get('accessToken');
    }

    const start = performance.now();
    const response = await fetch(transformedUrl, {
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'client-id': import.meta.env.VITE_CLIENT_ID,
      },
      ...(signal && { signal }),
    });
    const duration = performance.now() - start;
    return response;
  });

  // Cache the successful response (if feature flag is enabled)

  if (isValidDataForCaching(data) && FeatureFlagStore.isApiCachingEnabled) {
    try {
      await singletonCacheAPIStore.setData(cacheKey, data);
    } catch (error) {
      console.warn('Cache API write error:', error);
      try {
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('component', 'webServiceProvider');
          scope.setExtra('cacheKey', cacheKey);
          scope.setExtra('errorMessage', error?.message);
          Sentry.captureMessage('Cache API write error');
        });
      } catch (_) {
        // Intentionally ignore Sentry failures to avoid impacting flow
      }
    }
  }

  return data;
}

async function _patch(endpoint, body) {
  const result = await _fetchWithMethod(endpoint, body, 'PATCH');
  await invalidateCacheAfterMutation('PATCH', endpoint);
  return result;
}

async function _post(endpoint, body, signal, shouldReturnErrResponse = false) {
  const result = await _fetchWithMethod(
    endpoint,
    body,
    'POST',
    signal,
    shouldReturnErrResponse
  );

  await invalidateCacheAfterMutation('POST', endpoint);
  return result;
}

async function _postFormData(endpoint, formData) {
  const data = await _fetch(endpoint, async (transformedUrl) => {
    let accessToken;

    if (AppStore.isOnGeotabDashboard()) {
      accessToken = await PitstopCookie.get('accessToken');
    } else {
      accessToken = PitstopCookie.get('accessToken');
    }

    const start = performance.now();
    const response = await fetch(transformedUrl, {
      body: formData,
      method: 'POST',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'client-id': import.meta.env.VITE_CLIENT_ID,
      },
    });
    const duration = performance.now() - start;
    return response;
  });

  await invalidateCacheAfterMutation('POST', endpoint);
  return data;
}

async function _fetchCSV(endpoint, params, signal) {
  const transformedUrl = _transformUrl(endpoint);
  const url = new URL(transformedUrl);
  url.search = new URLSearchParams(params);

  let accessToken;

  if (AppStore.isOnGeotabDashboard()) {
    accessToken = await PitstopCookie.get('accessToken');
  } else {
    accessToken = PitstopCookie.get('accessToken');
  }

  const start = performance.now();
  const response = await fetch(url, {
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'text/csv',
      'client-id': import.meta.env.VITE_CLIENT_ID,
    },
    ...(signal && { signal }),
  });
  const duration = performance.now() - start;

  if (!response.ok) {
    throw response.statusText;
  }

  const data = await response.blob();
  return data;
}

async function getPDF(endpoint, signal) {
  let accessToken;

  if (AppStore.isOnGeotabDashboard()) {
    accessToken = await PitstopCookie.get('accessToken');
  } else {
    accessToken = PitstopCookie.get('accessToken');
  }

  const start = performance.now();
  const resp = await fetch(_transformUrl(endpoint), {
    method: 'GET',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/pdf',
      'client-id': import.meta.env.VITE_CLIENT_ID,
    },
    ...(signal && { signal }),
  });
  const duration = performance.now() - start;
  if (!resp.ok) {
    throw resp.statusText;
  }
  const data = await resp.blob();
  return data;
}

async function _put(endpoint, body) {
  const result = await _fetchWithMethod(endpoint, body, 'PUT');
  await invalidateCacheAfterMutation('PUT', endpoint);
  return result;
}

function _transformUrl(url) {
  return `${_apiBaseUrl}${_prefix}${url}`;
}
